import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FcSettings, FcOrgUnit, FcDocument, FcAddRow } from "react-icons/fc";
import { HiMenuAlt3, HiX } from "react-icons/hi";
import { AlertError } from "./Alert";
import { useAuth } from "../authContext";
import pkg from "../../package.json";

interface LayoutProps {
  children: React.ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  const appVersion = pkg.version;
  const navigate = useNavigate();
  const { userProfile, signOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const is_carlist = userProfile?.is_carlist ?? 0;
  const is_newcar = userProfile?.is_newcar ?? 0;
  const is_report = userProfile?.is_report ?? 0;
  const is_setting = userProfile?.is_setting ?? 0;

  const pages = [
    {
      page: "รายการรถ",
      icon: <FcOrgUnit size="22" />,
      route: "/home",
      perm: is_carlist,
      permLabel: "ดูรายการรถ",
    },
    {
      page: "เพิ่มรถใหม่",
      icon: <FcAddRow size="22" />,
      route: "/newcar",
      perm: is_newcar,
      permLabel: "เพิ่ม/แก้ไขรถ",
    },
    {
      page: "รายงานสรุป",
      icon: <FcDocument size="22" />,
      route: "/report",
      perm: is_report,
      permLabel: "ดูรายงาน",
    },
    {
      page: "ตั้งค่า",
      icon: <FcSettings size="22" />,
      route: "/setting",
      perm: is_setting,
      permLabel: "ตั้งค่า",
    },
  ];

  function handleNav(page: (typeof pages)[number]) {
    if (page.perm === 0) {
      AlertError("สิทธิ์ของท่านไม่สามารถ" + page.permLabel + "ได้");
      return;
    }
    navigate(page.route);
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Navbar */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14">
            {/* Logo + Company */}
            <div className="flex items-center gap-2 shrink-0">
              <img
                src="/images/logo.png"
                alt="StockCars"
                className="h-10 w-auto"
              />
              <div className="hidden sm:block">
                <div className="text-lg font-bold text-gray-900 leading-tight">
                  StockCars
                </div>
                <div className="text-xs text-gray-500">
                  {userProfile?.company_name || ""}
                </div>
              </div>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-1">
              {pages.map((page) => (
                <button
                  key={page.page}
                  onClick={() => handleNav(page)}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-gray-700 rounded-md hover:bg-gray-100 transition-colors"
                >
                  {page.icon}
                  {page.page}
                </button>
              ))}
            </div>

            {/* User + Logout */}
            <div className="flex items-center gap-3">
              <span className="hidden sm:inline text-sm text-gray-500">
                {userProfile?.email || userProfile?.fullname || ""}
              </span>
              <button
                onClick={signOut}
                className="px-3 py-1.5 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-700 transition-colors"
              >
                LOG OUT
              </button>

              {/* Mobile Hamburger */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-1.5 rounded-md text-gray-600 hover:bg-gray-100"
              >
                {mobileMenuOpen ? <HiX size="24" /> : <HiMenuAlt3 size="24" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white">
            <div className="px-4 py-2 space-y-1">
              {pages.map((page) => (
                <button
                  key={page.page}
                  onClick={() => {
                    handleNav(page);
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center gap-2 w-full px-3 py-2.5 text-sm font-semibold text-gray-700 rounded-md hover:bg-gray-100"
                >
                  {page.icon}
                  {page.page}
                </button>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Content */}
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 py-4">
        {children}
      </main>

      {/* Footer */}
      <footer className="max-w-7xl mx-auto px-4 sm:px-6 pb-3">
        <div className="flex justify-between items-center text-xs text-gray-400">
          <span>&copy; {new Date().getFullYear()} StockCars </span>
          <span>v{appVersion}</span>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
